/* OrganisationHealthPanel.jsx — Multi-office deployment infrastructure.
 *
 * Surfaces organisation-wide deployment health, office composite scoring,
 * regional hierarchy, rollout progress, policy consistency. Lives in
 * Mission Control with a "Full view" CTA to /organisation.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Buildings, Building2, Network, ShieldCheck, Sparkles, ArrowUpRight,
  GitBranch, BookOpen, Settings2, Trophy, AlertOctagon, Workflow,
} from "lucide-react";
import { api } from "../../lib/api";
import { MissionPanelSkeleton } from "../ui/operational-skeleton";

const ICONS = { Building2, ShieldCheck, Sparkles, GitBranch, BookOpen, Workflow };

const BAND_TONE = {
  Strong:     { fg: "#047857", bg: "rgba(16,185,129,0.14)",  dot: "#10B981" },
  Stable:     { fg: "#4338CA", bg: "rgba(99,102,241,0.10)",  dot: "#818CF8" },
  Developing: { fg: "#92400E", bg: "rgba(245,158,11,0.12)",  dot: "#F59E0B" },
  Watching:   { fg: "#9F1239", bg: "rgba(244,63,94,0.10)",   dot: "#F43F5E" },
};

/* ─── Hero ─────────────────────────────────────────────────────── */
const Hero = ({ summary, narrative }) => (
  <div
    className="relative overflow-hidden rounded-2xl px-5 sm:px-6 py-5 mb-5"
    data-testid="org-hero"
    style={{
      background: "linear-gradient(135deg, #050818 0%, #181E48 55%, #2E2570 100%)",
      boxShadow: "0 24px 60px -32px rgba(15,23,42,0.55), inset 0 0 0 1px rgba(255,255,255,0.05)",
    }}
  >
    <span aria-hidden className="absolute -top-10 -left-8 w-[260px] h-[260px] rounded-full blur-3xl pointer-events-none"
      style={{ background: "radial-gradient(circle, rgba(167,139,250,0.32), transparent 65%)" }} />
    <div className="relative">
      <div className="inline-flex items-center gap-2 mb-2">
        <Network size={12} className="text-violet-300" strokeWidth={2.3} />
        <p className="text-[9.5px] uppercase tracking-[0.22em] font-bold text-violet-200/90">
          Organisation Infrastructure
        </p>
      </div>
      <p className="text-[13px] text-white/85 leading-relaxed max-w-2xl mb-4"
         data-testid="org-narrative">{narrative}</p>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5">
        <Stat label="Offices"     value={summary?.office_count ?? 0}   accent="#A78BFA" testid="org-stat-offices" />
        <Stat label="Regions"     value={summary?.region_count ?? 0}   accent="#818CF8" testid="org-stat-regions" />
        <Stat label="Team"        value={summary?.team_total ?? 0}     accent="#38BDF8" testid="org-stat-team" />
        <Stat label="Avg health"  value={`${summary?.avg_composite ?? 0}/100`} accent="#10B981" testid="org-stat-health" />
        <Stat label="Consistency" value={`${summary?.policy_consistency_pct ?? 0}%`}    accent="#F59E0B" testid="org-stat-consistency" />
      </div>
    </div>
  </div>
);

const Stat = ({ label, value, accent, testid }) => (
  <div className="rounded-lg px-3 py-2.5"
    style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)" }}
    data-testid={testid}>
    <p className="text-[9px] uppercase tracking-[0.16em] font-bold text-white/55">{label}</p>
    <p className="text-[16px] font-semibold tabular-nums" style={{ color: accent }}>{value}</p>
  </div>
);

const SubHeader = ({ icon: Icon, eyebrow, title }) => (
  <div className="flex items-center gap-2 mb-2.5">
    <div className="w-6 h-6 rounded-md inline-flex items-center justify-center"
      style={{ background: "rgba(99,102,241,0.10)", color: "#4338CA" }}>
      <Icon size={11} strokeWidth={2.3} />
    </div>
    <div className="min-w-0">
      <p className="text-[9.5px] uppercase tracking-[0.16em] font-bold text-violet-700">{eyebrow}</p>
      <h3 className="text-[12.5px] font-semibold text-slate-900 truncate">{title}</h3>
    </div>
  </div>
);

/* ─── Office Row ───────────────────────────────────────────────── */
const OfficeRow = ({ o }) => {
  const t = BAND_TONE[o.band] || BAND_TONE.Stable;
  return (
    <div className="flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-slate-50/70 transition-colors"
         data-testid={`org-office-${o.office_id}`}>
      <div className="w-9 h-9 rounded-lg inline-flex items-center justify-center shrink-0"
        style={{ background: t.bg, color: t.fg }}>
        <Building2 size={14} strokeWidth={2.3} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[12.5px] font-semibold text-slate-900 truncate">{o.name}</p>
        <div className="flex items-center gap-2 mt-0.5">
          <p className="text-[10.5px] text-slate-500 truncate">
            {o.team_size} team · {o.active_deals} deals
          </p>
          <span aria-hidden className="w-1 h-1 rounded-full bg-slate-300" />
          <p className="text-[10.5px] text-slate-500">{o.rollout_pct}% rolled out</p>
        </div>
      </div>
      <span className="text-[10.5px] font-semibold px-2 py-0.5 rounded-full shrink-0"
        style={{ background: t.bg, color: t.fg }}>
        {o.band}
      </span>
      <p className="text-[13px] font-semibold tabular-nums w-10 text-right shrink-0"
         style={{ color: t.fg }}>
        {o.composite_score}
      </p>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   Main Panel
   ─────────────────────────────────────────────────────────────────── */
export const OrganisationHealthPanel = () => {
  const nav = useNavigate();
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const { data: resp } = await api.get("/api/organisation/overview");
        if (alive) setData(resp?.organisation || null);
      } catch (err) {
        if (alive) setError(err?.response?.status === 403
          ? "Leadership only."
          : "Could not load Organisation Infrastructure.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (error) return null;
  if (loading) return <MissionPanelSkeleton tone="light" testid="org-section-loading" />;
  if (!data) return null;

  const offices = data.health?.offices || [];
  const regions = data.hierarchy?.regions || [];

  return (
    <section className="cf-hydrate-fade mb-7" data-testid="mission-control-organisation">
      <div className="flex items-center gap-2.5 mb-3.5">
        <div className="w-7 h-7 rounded-md inline-flex items-center justify-center"
          style={{ background: "rgba(99,102,241,0.10)", color: "#4338CA" }}>
          <Network size={13} strokeWidth={2.3} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-[0.16em] font-bold text-violet-700">
            Organisation Health
          </p>
          <h2 className="text-[13.5px] font-semibold text-slate-900 truncate">
            Multi-office deployment, policy consistency, rollout progress
          </h2>
        </div>
        <button
          type="button"
          onClick={() => nav("/organisation")}
          className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md text-[10.5px] font-semibold"
          style={{
            background: "rgba(99,102,241,0.10)", color: "#4338CA",
            border: "1px solid rgba(99,102,241,0.22)",
          }}
          data-testid="org-open-full">
          Full view
          <ArrowUpRight size={10} strokeWidth={2.4} />
        </button>
      </div>

      <Hero summary={data.summary} narrative={data.narrative} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="gp-card p-4" data-testid="org-offices-list">
          <SubHeader icon={Building2} eyebrow="Office Health" title={`${offices.length} offices`} />
          <div className="space-y-1">
            {offices.map((o) => <OfficeRow key={o.office_id} o={o} />)}
            {offices.length === 0 ? (
              <p className="text-[11.5px] text-slate-500 italic px-2">
                Offices will appear once deployed.
              </p>
            ) : null}
          </div>
        </div>
        <div className="gp-card p-4" data-testid="org-hierarchy">
          <SubHeader icon={GitBranch} eyebrow="Regional Hierarchy"
            title={`${regions.length} region${regions.length === 1 ? "" : "s"}`} />
          <div className="space-y-3">
            {regions.map((r) => (
              <div key={r.name} data-testid={`org-region-${r.name}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-[12px] font-semibold text-slate-800">{r.name}</p>
                  <p className="text-[10.5px] text-slate-500">
                    {r.office_count} office{r.office_count === 1 ? "" : "s"} · {r.team_total} team
                  </p>
                </div>
                <div className="space-y-0.5 pl-3 border-l-2 border-violet-100">
                  {(r.offices || []).map((o) => (
                    <p key={o.office_id} className="text-[11.5px] text-slate-600 truncate">
                      · {o.name} <span className="text-slate-400">({o.active_deals} deals)</span>
                    </p>
                  ))}
                </div>
              </div>
            ))}
            {regions.length === 0 ? (
              <p className="text-[11.5px] text-slate-500 italic">
                Regional hierarchy will populate as offices are organised.
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
};

export default OrganisationHealthPanel;
